/**
 * Canvas 2D rendering.
 *
 * `draw()` always paints once, synchronously — that's the path every state
 * change already takes (game.ts calls it after every move). Decorative
 * animation (path glow easing, tile press pulse, win particles, the deep-sky
 * win sequence) rides a second, self-terminating loop: `ensureLoop()` only
 * schedules requestAnimationFrame while something is actually mid-animation,
 * and the loop stops itself the frame nothing is left alive. Idle boards cost
 * zero rAF callbacks.
 *
 * Every animated piece of state lives in fixed-size fields or typed arrays
 * allocated once in the constructor — the paint path itself allocates
 * nothing, which is what keeps this cheap on a mid-range phone.
 *
 * The canvas backing store is sized to CSS box * devicePixelRatio so strokes
 * stay crisp on retina/iPad screens; `resize()` must be called whenever the
 * CSS box changes, before `draw()`.
 *
 * Deep-sky reveal: everything except the sky photo itself — tiles, holes,
 * walls, the path, both world markers, the rocket — is drawn into an
 * offscreen "chrome" canvas every frame, then composited over the sky at one
 * shared alpha. That's what lets the whole board fade away as a single piece
 * once a level is solved, leaving nothing but the photo. A level with no
 * curated photo yet (most of the schedule, until the catalogue is complete)
 * still gets the full path/rocket/marker treatment; it just fades to a flat
 * background instead of a photograph, and skips the reveal step entirely.
 */

import { colOf, rowOf, type Board } from "../../scripts/lib/board.ts";
import type { DeepSkyEntry, Level, NodeId } from "../../scripts/lib/types.ts";
import { tileCenter, type Layout } from "./layout.ts";
import { reducedMotion } from "../motion.ts";
import { asset } from "../paths.ts";

const COLORS = {
  background: "#0a0d1c",
  tile: "#1a2140",
  tileGap: 0.07, // fraction of tile size left as gutter between tiles
  wall: "#eef1ff",
  path: "#5fd4ff",
  pathGlow: "rgba(95, 212, 255, 0.55)",
  gold: "#ffe28a",
  markerText: "#06090f",
  press: "rgba(200, 243, 255, 0.35)",
};

/** How long the newest path segment takes to reach full width. */
const GROW_DURATION_MS = 150;
/** How long a tile-press pulse takes to fade. */
const PRESS_DURATION_MS = 260;

const MAX_PARTICLES = 48;
const PARTICLE_LIFE_MS = 850;
const PARTICLE_COLORS = ["#5fd4ff", "#c8f3ff", "#33e59a", "#ffb238"];

/** The post-win sequence: retrace the path bright, spin at the destination, then fade the chrome away. */
const TRACE_MS = 1200;
const SPIN_MS = 950;
const REVEAL_MS = 750;

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function easeOutCubic(t: number): number {
  const u = 1 - t;
  return 1 - u * u * u;
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

type Point = { x: number; y: number };

/** A point (and local tangent angle) at fraction `t` along a polyline, by arc length. */
function pointAtFraction(pts: readonly Point[], t: number): { point: Point; angle: number } {
  if (pts.length < 2) return { point: pts[0] ?? { x: 0, y: 0 }, angle: 0 };
  const lens: number[] = [];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    lens.push(d);
    total += d;
  }
  let target = t * total;
  for (let i = 0; i < lens.length; i++) {
    if (target <= lens[i] || i === lens.length - 1) {
      const segT = lens[i] === 0 ? 0 : Math.min(1, target / lens[i]);
      const a = pts[i];
      const b = pts[i + 1];
      return {
        point: { x: a.x + (b.x - a.x) * segT, y: a.y + (b.y - a.y) * segT },
        angle: Math.atan2(b.y - a.y, b.x - a.x),
      };
    }
    target -= lens[i];
  }
  return { point: pts[pts.length - 1], angle: 0 };
}

export interface DrawState {
  level: Level;
  board: Board;
  layout: Layout;
  path: NodeId[];
  /** Hides the grab halo while a stroke is in progress. */
  dragging: boolean;
}

type WinPhase = "trace" | "spin" | "reveal";
interface WinAnim {
  phase: WinPhase;
  start: number;
}

export class Renderer {
  private readonly ctx: CanvasRenderingContext2D;
  private lastState: DrawState | null = null;

  private rafId: number | null = null;

  // Tip-segment grow animation: retriggered whenever path length increases.
  private lastPathLength = 0;
  private growStart = 0;

  // Tile press pulse: at most one at a time (single active drag).
  private pressId: NodeId | null = null;
  private pressStart = 0;

  // Particle pool — structure-of-arrays, reused every burst, zero per-frame allocation.
  private readonly pX = new Float32Array(MAX_PARTICLES);
  private readonly pY = new Float32Array(MAX_PARTICLES);
  private readonly pVX = new Float32Array(MAX_PARTICLES);
  private readonly pVY = new Float32Array(MAX_PARTICLES);
  private readonly pBornAt = new Float32Array(MAX_PARTICLES).fill(-Infinity);
  private readonly pColor = new Uint8Array(MAX_PARTICLES);
  private particleCursor = 0;

  // Deep-sky reveal.
  private readonly chrome: HTMLCanvasElement;
  private readonly chromeCtx: CanvasRenderingContext2D;
  private readonly sky: HTMLCanvasElement;
  private readonly skyCtx: CanvasRenderingContext2D;
  private skyReady = false;
  private skyUrl: string | null = null;
  private skyImage: HTMLImageElement | null = null;
  private winAnim: WinAnim | null = null;
  /** True once the photo has fully faded into view — chrome (tiles/path/markers) is hidden. */
  private revealed = false;
  /** True once the post-win animation has finished, photo or not — stops the idle shimmer/flicker loop. */
  private settled = false;
  private onWinAnimDone: (() => void) | null = null;
  private cssWidth = 0;
  private cssHeight = 0;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2d canvas context unavailable");
    this.ctx = ctx;
    this.chrome = document.createElement("canvas");
    const chromeCtx = this.chrome.getContext("2d");
    if (!chromeCtx) throw new Error("2d canvas context unavailable");
    this.chromeCtx = chromeCtx;
    this.sky = document.createElement("canvas");
    const skyCtx = this.sky.getContext("2d");
    if (!skyCtx) throw new Error("2d canvas context unavailable");
    this.skyCtx = skyCtx;
  }

  /** Size the backing store to the CSS box * DPR. Call before draw(). */
  resize(cssWidth: number, cssHeight: number): void {
    const dpr = window.devicePixelRatio || 1;
    this.cssWidth = cssWidth;
    this.cssHeight = cssHeight;
    this.canvas.style.width = `${cssWidth}px`;
    this.canvas.style.height = `${cssHeight}px`;
    this.canvas.width = Math.max(1, Math.round(cssWidth * dpr));
    this.canvas.height = Math.max(1, Math.round(cssHeight * dpr));
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.chrome.width = this.canvas.width;
    this.chrome.height = this.canvas.height;
    this.chromeCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.sky.width = this.canvas.width;
    this.sky.height = this.canvas.height;
    this.skyCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (this.skyImage) this.paintSkyFromImage(this.skyImage);

    if (this.lastState) this.paint(performance.now());
  }

  /**
   * Set (or clear) the object this level's photo reveals. Loads the image
   * once per URL and re-composites the offscreen sky canvas when it's ready.
   * Safe to call with the same entry repeatedly — it no-ops unless the image
   * URL actually changed.
   */
  setDeepSky(entry: DeepSkyEntry | null): void {
    this.revealed = false;
    this.settled = false;
    this.winAnim = null;
    const url = entry?.image ?? null;
    if (url === this.skyUrl) {
      if (!url) this.skyReady = false;
      return;
    }
    this.skyUrl = url;
    this.skyReady = false;
    this.skyImage = null;
    if (!url) return;
    const img = new Image();
    img.onload = () => {
      if (this.skyUrl !== url) return; // superseded by a later call
      this.skyImage = img;
      this.paintSkyFromImage(img);
      this.skyReady = true;
      if (this.lastState) this.paint(performance.now());
    };
    // The catalogue stores site-root paths ("/images/deepsky/x.jpg"); resolve
    // them against the deployment base so the photos load when the game is
    // embedded under a subpath.
    img.src = asset(url);
  }

  private paintSkyFromImage(img: HTMLImageElement): void {
    const { cssWidth, cssHeight } = this;
    if (cssWidth === 0 || cssHeight === 0) return;
    const scale = Math.max(cssWidth / img.width, cssHeight / img.height);
    const dw = img.width * scale;
    const dh = img.height * scale;
    const dx = (cssWidth - dw) / 2;
    const dy = (cssHeight - dh) / 2;
    this.skyCtx.fillStyle = "#050611";
    this.skyCtx.fillRect(0, 0, cssWidth, cssHeight);
    this.skyCtx.drawImage(img, dx, dy, dw, dh);
    const vg = this.skyCtx.createRadialGradient(
      cssWidth / 2,
      cssHeight / 2,
      Math.min(cssWidth, cssHeight) * 0.25,
      cssWidth / 2,
      cssHeight / 2,
      Math.max(cssWidth, cssHeight) * 0.72,
    );
    vg.addColorStop(0, "rgba(5,6,17,0)");
    vg.addColorStop(1, "rgba(5,6,17,0.55)");
    this.skyCtx.fillStyle = vg;
    this.skyCtx.fillRect(0, 0, cssWidth, cssHeight);
  }

  draw(state: DrawState): void {
    if (!reducedMotion() && state.path.length > this.lastPathLength) {
      this.growStart = performance.now();
    }
    this.lastPathLength = state.path.length;
    this.lastState = state;
    this.paint(performance.now());
    this.ensureLoop();
  }

  /** Brief highlight under a tile the pointer just landed on. */
  pulseTile(id: NodeId): void {
    if (reducedMotion()) return;
    this.pressId = id;
    this.pressStart = performance.now();
    this.ensureLoop();
  }

  /** Particle burst centered on a tile. */
  burst(id: NodeId): void {
    if (reducedMotion() || !this.lastState) return;
    const { level, layout } = this.lastState;
    const { x, y } = tileCenter(layout, rowOf(level.cols, id), colOf(level.cols, id));
    const now = performance.now();
    const count = Math.min(MAX_PARTICLES, 32);
    for (let i = 0; i < count; i++) {
      const slot = this.particleCursor;
      this.particleCursor = (this.particleCursor + 1) % MAX_PARTICLES;
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const speed = layout.tileSize * (2.2 + Math.random() * 1.6);
      this.pX[slot] = x;
      this.pY[slot] = y;
      this.pVX[slot] = Math.cos(angle) * speed;
      this.pVY[slot] = Math.sin(angle) * speed;
      this.pBornAt[slot] = now;
      this.pColor[slot] = i % PARTICLE_COLORS.length;
    }
    this.ensureLoop();
  }

  /** Undo a completed reveal — restores the board's tiles. Call on restart/undo past the win. */
  resetWinState(): void {
    this.winAnim = null;
    this.revealed = false;
    this.settled = false;
    this.onWinAnimDone = null;
  }

  /**
   * Play the post-win sequence — retrace the path bright, spin at the
   * destination, then (if this level has a photo) fade the whole board away
   * to reveal it — and call `onDone` once it's finished. Skips straight to
   * `onDone` under reduced motion.
   */
  startWinAnimation(onDone: () => void): void {
    if (reducedMotion()) {
      this.revealed = this.skyReady;
      this.settled = true;
      onDone();
      return;
    }
    this.onWinAnimDone = onDone;
    this.winAnim = { phase: "trace", start: performance.now() };
    this.ensureLoop();
  }

  private ensureLoop(): void {
    if (this.rafId !== null) return;
    const step = (now: number) => {
      this.paint(now);
      this.rafId = this.isAnimating(now) ? requestAnimationFrame(step) : null;
    };
    this.rafId = requestAnimationFrame(step);
  }

  private isAnimating(now: number): boolean {
    if (this.winAnim) return true;
    if (now - this.growStart < GROW_DURATION_MS) return true;
    if (this.pressId !== null && now - this.pressStart < PRESS_DURATION_MS) return true;
    for (let i = 0; i < MAX_PARTICLES; i++) {
      if (now - this.pBornAt[i] < PARTICLE_LIFE_MS) return true;
    }
    // A live path keeps the rocket's flame flicker and the path shimmer alive,
    // until the post-win animation (if any) has settled.
    if (this.lastState && this.lastState.path.length > 0 && !this.settled) return true;
    return false;
  }

  private paint(now: number): void {
    if (!this.lastState) return;
    const { level, board, layout, path, dragging } = this.lastState;
    const g = this.chromeCtx;
    const { cols } = level;
    const { tileSize } = layout;
    const cssWidth = this.cssWidth;
    const cssHeight = this.cssHeight;

    // --- Win-animation state machine ---
    let traceT = 0;
    let spinProgress = 0;
    let cometPos: { point: Point; angle: number } | null = null;
    let chromeAlpha = this.revealed ? 0 : 1;

    if (this.winAnim) {
      const elapsed = now - this.winAnim.start;
      if (this.winAnim.phase === "trace") {
        traceT = Math.min(1, elapsed / TRACE_MS);
        const pts = path.map((id) => tileCenter(layout, rowOf(cols, id), colOf(cols, id)));
        cometPos = pointAtFraction(pts, easeInOutCubic(traceT));
        if (traceT >= 1) this.winAnim = { phase: "spin", start: now };
      } else if (this.winAnim.phase === "spin") {
        traceT = 1;
        spinProgress = Math.min(1, elapsed / SPIN_MS);
        if (spinProgress === 0) this.burst(level.exit);
        if (spinProgress >= 1) {
          if (this.skyReady) {
            this.winAnim = { phase: "reveal", start: now };
          } else {
            this.winAnim = null;
            this.settled = true;
            this.onWinAnimDone?.();
            this.onWinAnimDone = null;
          }
        }
      } else {
        traceT = 1;
        spinProgress = 1;
        const revealT = Math.min(1, elapsed / REVEAL_MS);
        chromeAlpha = 1 - revealT ** 3;
        if (revealT >= 1) {
          this.winAnim = null;
          this.revealed = true;
          this.settled = true;
          this.onWinAnimDone?.();
          this.onWinAnimDone = null;
        }
      }
    }

    // --- Chrome layer: tiles, holes, walls, path, markers, rocket, particles. ---
    g.clearRect(0, 0, cssWidth, cssHeight);

    const gap = tileSize * COLORS.tileGap;
    const radius = Math.max(3, tileSize * 0.14);
    const hasPhoto = this.skyReady;
    const visited = new Set(path);

    // Tile press pulse — drawn under the tiles so it reads as a glow, not an overlay.
    if (this.pressId !== null) {
      const elapsed = now - this.pressStart;
      if (elapsed < PRESS_DURATION_MS) {
        const t = elapsed / PRESS_DURATION_MS;
        const { x, y } = tileCenter(layout, rowOf(cols, this.pressId), colOf(cols, this.pressId));
        g.fillStyle = COLORS.press;
        g.globalAlpha = 1 - t;
        g.beginPath();
        g.arc(x, y, tileSize * (0.45 + t * 0.5), 0, Math.PI * 2);
        g.fill();
        g.globalAlpha = 1;
      } else {
        this.pressId = null;
      }
    }

    // Tiles. With a photo loaded, an unvisited tile stays opaque (hiding the
    // sky) and a visited one is skipped entirely, letting the photo show
    // through where the path has already been. With no photo yet, every
    // playable tile stays opaque regardless — the original always-on look.
    g.fillStyle = COLORS.tile;
    for (const id of board.playable) {
      if (hasPhoto && visited.has(id)) continue;
      const r = rowOf(cols, id);
      const c = colOf(cols, id);
      const x = c * tileSize + gap;
      const y = r * tileSize + gap;
      const size = tileSize - gap * 2;
      roundRect(g, x, y, size, size, radius);
      g.fill();
    }

    // Walls — thick segments drawn on the shared border between two tiles.
    g.strokeStyle = COLORS.wall;
    g.lineCap = "round";
    g.lineWidth = Math.max(3, tileSize * 0.12);
    for (const [a, b] of level.walls) {
      const ra = rowOf(cols, a);
      const ca = colOf(cols, a);
      const rb = rowOf(cols, b);
      const cb = colOf(cols, b);
      g.beginPath();
      if (ra === rb) {
        const x = Math.max(ca, cb) * tileSize;
        const yTop = ra * tileSize;
        g.moveTo(x, yTop + gap);
        g.lineTo(x, yTop + tileSize - gap);
      } else {
        const y = Math.max(ra, rb) * tileSize;
        const xLeft = ca * tileSize;
        g.moveTo(xLeft + gap, y);
        g.lineTo(xLeft + tileSize - gap, y);
      }
      g.stroke();
    }

    this.drawPath(g, level, layout, path, now, traceT);

    this.drawEarth(g, level.start, layout);
    const planetGlow = spinProgress > 0 ? 0.5 + 0.5 * Math.sin(spinProgress * Math.PI) : 0;
    this.drawPlanet(g, level.exit, layout, planetGlow);

    // The grab zone around the rocket. A tap anywhere inside this starts or
    // resumes the stroke (see GameController.hitTest), and without something
    // drawn there nobody would know — players would keep aiming carefully at
    // a tile-sized target.
    //
    // Deliberately static rather than pulsing: a pulse would need a
    // requestAnimationFrame loop running the whole time the board sits idle,
    // and this renderer's rule is that an idle board costs zero rAF
    // callbacks. A steady halo is just as discoverable and free.
    if (!dragging && !this.winAnim && !this.revealed) {
      const head = path.length > 0 ? path[path.length - 1] : level.start;
      const c = tileCenter(layout, rowOf(cols, head), colOf(cols, head));
      const radius = tileSize * 0.95;
      const halo = g.createRadialGradient(c.x, c.y, tileSize * 0.25, c.x, c.y, radius);
      halo.addColorStop(0, "rgba(95,212,255,0.19)");
      halo.addColorStop(1, "rgba(95,212,255,0)");
      g.fillStyle = halo;
      g.beginPath();
      g.arc(c.x, c.y, radius, 0, Math.PI * 2);
      g.fill();
    }

    // Rocket: mid-flight during the victory-lap trace, spinning in place on
    // arrival, otherwise parked at the path head.
    const flicker = reducedMotion() ? 0.5 : 0.5 + 0.5 * Math.sin(now / 90);
    if (this.winAnim?.phase === "trace" && cometPos) {
      this.drawRocket(g, cometPos.point.x, cometPos.point.y, cometPos.angle, layout, flicker);
    } else if (this.winAnim && (this.winAnim.phase === "spin" || this.winAnim.phase === "reveal")) {
      const p = tileCenter(layout, rowOf(cols, level.exit), colOf(cols, level.exit));
      const lastAngle =
        path.length > 1
          ? Math.atan2(
              tileCenter(layout, rowOf(cols, path[path.length - 1]), colOf(cols, path[path.length - 1])).y -
                tileCenter(layout, rowOf(cols, path[path.length - 2]), colOf(cols, path[path.length - 2])).y,
              tileCenter(layout, rowOf(cols, path[path.length - 1]), colOf(cols, path[path.length - 1])).x -
                tileCenter(layout, rowOf(cols, path[path.length - 2]), colOf(cols, path[path.length - 2])).x,
            )
          : 0;
      const spinAngle = lastAngle + easeOutCubic(spinProgress) * Math.PI * 4;
      this.drawRocket(g, p.x, p.y, spinAngle, layout, flicker);
    } else if (path.length > 0) {
      const head = path[path.length - 1];
      const prev = path.length > 1 ? path[path.length - 2] : undefined;
      const p = tileCenter(layout, rowOf(cols, head), colOf(cols, head));
      let angle = -Math.PI / 2;
      if (prev !== undefined) {
        const q = tileCenter(layout, rowOf(cols, prev), colOf(cols, prev));
        angle = Math.atan2(p.y - q.y, p.x - q.x);
      }
      this.drawRocket(g, p.x, p.y, angle, layout, flicker);
    } else {
      const p = tileCenter(layout, rowOf(cols, level.start), colOf(cols, level.start));
      this.drawRocket(g, p.x, p.y, -Math.PI / 2, layout, flicker);
    }

    this.drawParticles(g, now);

    // --- Composite: the sky (photo or flat background) is always full
    // strength; the chrome fades away as one piece once a solved level's
    // photo is ready to reveal. ---
    this.ctx.clearRect(0, 0, cssWidth, cssHeight);
    if (hasPhoto) {
      this.ctx.drawImage(this.sky, 0, 0, this.sky.width, this.sky.height, 0, 0, cssWidth, cssHeight);
    } else {
      this.ctx.fillStyle = COLORS.background;
      this.ctx.fillRect(0, 0, cssWidth, cssHeight);
    }
    if (chromeAlpha > 0) {
      this.ctx.globalAlpha = chromeAlpha;
      this.ctx.drawImage(this.chrome, 0, 0, this.chrome.width, this.chrome.height, 0, 0, cssWidth, cssHeight);
      this.ctx.globalAlpha = 1;
    }
  }

  /**
   * The path as a glowing, shimmering polyline: a soft outer glow, a mid
   * stroke carrying a slowly drifting gradient, and a bright core down the
   * centre, plus small sparks of light travelling tail to head on a loop.
   * During the post-win victory lap, a gold overlay retraces the fraction
   * `traceT` of the path already covered.
   */
  private drawPath(
    g: CanvasRenderingContext2D,
    level: Level,
    layout: Layout,
    path: NodeId[],
    now: number,
    traceT: number,
  ): void {
    if (path.length < 2) return;
    const { cols } = level;
    const { tileSize } = layout;
    const pts = path.map((id) => tileCenter(layout, rowOf(cols, id), colOf(cols, id)));
    const tracePath = () => pts.forEach((p, i) => (i === 0 ? g.moveTo(p.x, p.y) : g.lineTo(p.x, p.y)));

    g.lineJoin = "round";
    g.lineCap = "round";

    const drift = Math.sin(now / 2600) * tileSize * 0.6;
    const w = this.cssWidth;
    const h = this.cssHeight;
    const shimmer = g.createLinearGradient(-drift, -drift, w + drift, h + drift);
    shimmer.addColorStop(0, "#3fa9ff");
    shimmer.addColorStop(0.5, "#eafcff");
    shimmer.addColorStop(1, "#9b7bff");

    g.shadowColor = COLORS.pathGlow;
    g.shadowBlur = tileSize * 0.4;
    g.strokeStyle = "rgba(95,212,255,0.35)";
    g.lineWidth = tileSize * 0.46;
    g.beginPath();
    tracePath();
    g.stroke();

    g.shadowBlur = tileSize * 0.2;
    g.strokeStyle = shimmer;
    g.lineWidth = tileSize * 0.3;
    g.beginPath();
    tracePath();
    g.stroke();

    g.shadowBlur = 0;
    g.strokeStyle = "rgba(255,255,255,0.85)";
    g.lineWidth = tileSize * 0.09;
    g.beginPath();
    tracePath();
    g.stroke();

    // Sparks travelling the line, tail to head, on a loop.
    const SPARKS = 4;
    for (let i = 0; i < SPARKS; i++) {
      const t = (((now / 1700 + i / SPARKS) % 1) + 1) % 1;
      const spark = pointAtFraction(pts, t);
      const twinkle = 0.5 + 0.5 * Math.sin(now / 220 + i * 1.7);
      g.shadowColor = "rgba(255,255,255,0.9)";
      g.shadowBlur = tileSize * 0.22;
      g.fillStyle = `rgba(255,255,255,${0.55 + 0.35 * twinkle})`;
      g.beginPath();
      g.arc(spark.point.x, spark.point.y, tileSize * (0.05 + 0.02 * twinkle), 0, Math.PI * 2);
      g.fill();
      g.shadowBlur = 0;
    }

    // Gold overlay for the traced portion, on the post-win victory lap.
    if (traceT > 0) {
      g.shadowColor = "rgba(255,226,138,0.85)";
      g.shadowBlur = tileSize * 0.32;
      g.strokeStyle = COLORS.gold;
      g.lineWidth = tileSize * 0.34;
      g.beginPath();
      const lens: number[] = [];
      let total = 0;
      for (let i = 1; i < pts.length; i++) {
        const d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
        lens.push(d);
        total += d;
      }
      const drawnLen = easeInOutCubic(traceT) * total;
      g.moveTo(pts[0].x, pts[0].y);
      let acc = 0;
      for (let i = 0; i < lens.length; i++) {
        if (acc + lens[i] <= drawnLen) {
          g.lineTo(pts[i + 1].x, pts[i + 1].y);
          acc += lens[i];
        } else {
          const segT = lens[i] === 0 ? 0 : (drawnLen - acc) / lens[i];
          const a = pts[i];
          const b = pts[i + 1];
          g.lineTo(a.x + (b.x - a.x) * segT, a.y + (b.y - a.y) * segT);
          break;
        }
      }
      g.stroke();
      g.shadowBlur = 0;
    }
  }

  /** Earth, always at the start tile: a small shaded sphere with continents and an atmosphere ring. */
  private drawEarth(g: CanvasRenderingContext2D, id: NodeId, layout: Layout): void {
    const cols = this.lastState!.level.cols;
    const p = tileCenter(layout, rowOf(cols, id), colOf(cols, id));
    const r = layout.tileSize * 0.24;
    g.save();
    g.translate(p.x, p.y);

    g.shadowColor = "rgba(120,190,255,0.45)";
    g.shadowBlur = layout.tileSize * 0.22;
    const ocean = g.createRadialGradient(-r * 0.35, -r * 0.35, r * 0.1, 0, 0, r);
    ocean.addColorStop(0, "#8fe3d8");
    ocean.addColorStop(0.45, "#2f8fd6");
    ocean.addColorStop(1, "#0e3f7a");
    g.fillStyle = ocean;
    g.beginPath();
    g.arc(0, 0, r, 0, Math.PI * 2);
    g.fill();
    g.shadowBlur = 0;

    g.save();
    g.beginPath();
    g.arc(0, 0, r, 0, Math.PI * 2);
    g.clip();
    g.fillStyle = "#3fae6a";
    g.beginPath();
    g.ellipse(-r * 0.25, -r * 0.15, r * 0.5, r * 0.32, 0.4, 0, Math.PI * 2);
    g.fill();
    g.beginPath();
    g.ellipse(r * 0.35, r * 0.35, r * 0.34, r * 0.22, -0.6, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "rgba(255,255,255,0.55)";
    g.beginPath();
    g.ellipse(r * 0.1, -r * 0.55, r * 0.4, r * 0.16, 0.2, 0, Math.PI * 2);
    g.fill();
    const shade = g.createLinearGradient(-r, r, r, -r);
    shade.addColorStop(0, "rgba(2,6,20,0.55)");
    shade.addColorStop(0.5, "rgba(2,6,20,0)");
    g.fillStyle = shade;
    g.fillRect(-r, -r, r * 2, r * 2);
    g.restore();

    g.strokeStyle = "rgba(140,210,255,0.55)";
    g.lineWidth = layout.tileSize * 0.035;
    g.beginPath();
    g.arc(0, 0, r * 1.18, 0, Math.PI * 2);
    g.stroke();

    g.restore();
  }

  /** The exit world, always at the exit tile: a small ringed planet, so it reads from the first frame. */
  private drawPlanet(g: CanvasRenderingContext2D, id: NodeId, layout: Layout, glow: number): void {
    const cols = this.lastState!.level.cols;
    const p = tileCenter(layout, rowOf(cols, id), colOf(cols, id));
    const r = layout.tileSize * 0.23;
    g.save();
    g.translate(p.x, p.y);

    if (glow > 0) {
      const halo = g.createRadialGradient(0, 0, 0, 0, 0, r * 2.6);
      halo.addColorStop(0, `rgba(255,184,122,${0.45 * glow})`);
      halo.addColorStop(1, "rgba(255,184,122,0)");
      g.fillStyle = halo;
      g.beginPath();
      g.arc(0, 0, r * 2.6, 0, Math.PI * 2);
      g.fill();
    }

    g.shadowColor = "rgba(255,154,77,0.55)";
    g.shadowBlur = layout.tileSize * 0.2;
    g.strokeStyle = "rgba(255,206,150,0.55)";
    g.lineWidth = layout.tileSize * 0.05;
    g.beginPath();
    g.ellipse(0, 0, r * 1.9, r * 0.6, -0.32, 0, Math.PI * 2);
    g.stroke();
    g.shadowBlur = 0;

    const body = g.createRadialGradient(-r * 0.3, -r * 0.3, r * 0.1, 0, 0, r);
    body.addColorStop(0, "#ffd9a0");
    body.addColorStop(0.55, "#ff9a4d");
    body.addColorStop(1, "#b5501a");
    g.fillStyle = body;
    g.beginPath();
    g.arc(0, 0, r, 0, Math.PI * 2);
    g.fill();

    g.save();
    g.beginPath();
    g.arc(0, 0, r, 0, Math.PI * 2);
    g.clip();
    g.strokeStyle = "rgba(120,50,10,0.25)";
    g.lineWidth = r * 0.16;
    for (const oy of [-0.5, 0, 0.55]) {
      g.beginPath();
      g.ellipse(0, oy * r, r * 1.05, r * 0.22, 0, 0, Math.PI * 2);
      g.stroke();
    }
    g.restore();

    g.strokeStyle = "rgba(255,225,180,0.85)";
    g.lineWidth = layout.tileSize * 0.045;
    g.beginPath();
    g.ellipse(0, r * 0.16, r * 1.9, r * 0.55, -0.32, Math.PI * 0.05, Math.PI * 0.95);
    g.stroke();

    g.restore();
  }

  /**
   * The crewed pod: rounded shell, landing legs, a glass dome and a warm
   * cabin light, rotated to face the direction of travel.
   *
   * The warm light inside the dome is the point of the design, not a
   * decoration — at tile size (~35px on a phone) the silhouette alone reads
   * as "a shape", and it's the glow that makes it read as *someone
   * travelling*. Everything else is sized around keeping that glow visible.
   */
  private drawRocket(
    g: CanvasRenderingContext2D,
    x: number,
    y: number,
    angle: number,
    layout: Layout,
    flameFlicker: number,
  ): void {
    g.save();
    g.translate(x, y);
    g.rotate(angle + Math.PI / 2);
    const s = layout.tileSize * 0.2;

    g.shadowColor = "rgba(95,212,255,0.8)";
    g.shadowBlur = layout.tileSize * 0.27;

    // Exhaust plume.
    const flameLen = s * (0.9 + flameFlicker * 0.4);
    const flameGrad = g.createLinearGradient(0, s * 0.9, 0, s * 0.9 + flameLen);
    flameGrad.addColorStop(0, "rgba(255,226,138,0.92)");
    flameGrad.addColorStop(1, "rgba(255,154,77,0)");
    g.fillStyle = flameGrad;
    g.beginPath();
    g.moveTo(-s * 0.3, s * 0.9);
    g.lineTo(0, s * 0.9 + flameLen);
    g.lineTo(s * 0.3, s * 0.9);
    g.closePath();
    g.fill();

    // Landing legs.
    g.strokeStyle = "#8fa0c4";
    g.lineWidth = s * 0.12;
    g.lineCap = "round";
    g.beginPath();
    g.moveTo(-s * 0.3, s * 0.5);
    g.lineTo(-s * 0.66, s * 1.05);
    g.stroke();
    g.beginPath();
    g.moveTo(s * 0.3, s * 0.5);
    g.lineTo(s * 0.66, s * 1.05);
    g.stroke();

    // Rounded capsule shell.
    const shell = g.createLinearGradient(-s * 0.5, -s * 0.6, s * 0.5, s * 0.8);
    shell.addColorStop(0, "#ffffff");
    shell.addColorStop(1, "#9fb0d0");
    g.fillStyle = shell;
    g.beginPath();
    g.moveTo(-s * 0.52, s * 0.55);
    g.quadraticCurveTo(-s * 0.62, -s * 0.75, 0, -s * 1.05);
    g.quadraticCurveTo(s * 0.62, -s * 0.75, s * 0.52, s * 0.55);
    g.quadraticCurveTo(0, s * 0.85, -s * 0.52, s * 0.55);
    g.closePath();
    g.fill();

    g.shadowBlur = 0;

    // Glass dome. The specular streak is what keeps it reading as glass
    // rather than as a hole cut in the shell.
    const domeR = s * 0.36;
    const dome = g.createRadialGradient(-domeR * 0.3, -s * 0.32 - domeR * 0.35, domeR * 0.05, 0, -s * 0.32, domeR);
    dome.addColorStop(0, "#cdf3ff");
    dome.addColorStop(0.5, "#3f9ad8");
    dome.addColorStop(1, "#0d2748");
    g.fillStyle = dome;
    g.beginPath();
    g.arc(0, -s * 0.32, domeR, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "rgba(255,255,255,0.6)";
    g.beginPath();
    g.ellipse(-domeR * 0.34, -s * 0.32 - domeR * 0.4, domeR * 0.26, domeR * 0.13, -0.7, 0, Math.PI * 2);
    g.fill();

    g.strokeStyle = "#eef1ff";
    g.lineWidth = s * 0.08;
    g.beginPath();
    g.arc(0, -s * 0.32, domeR, 0, Math.PI * 2);
    g.stroke();

    // The occupant's light.
    const glow = g.createRadialGradient(-s * 0.1, -s * 0.42, 0, -s * 0.1, -s * 0.42, s * 0.3);
    glow.addColorStop(0, "rgba(255,226,138,0.6)");
    glow.addColorStop(1, "rgba(255,196,110,0)");
    g.fillStyle = glow;
    g.beginPath();
    g.arc(-s * 0.1, -s * 0.42, s * 0.3, 0, Math.PI * 2);
    g.fill();

    g.restore();
  }

  private drawParticles(g: CanvasRenderingContext2D, now: number): void {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      const age = now - this.pBornAt[i];
      if (age < 0 || age >= PARTICLE_LIFE_MS) continue;
      const t = age / PARTICLE_LIFE_MS;
      const dragFactor = 1 - t * 0.7;
      const x = this.pX[i] + this.pVX[i] * (age / 1000) * dragFactor;
      const y = this.pY[i] + this.pVY[i] * (age / 1000) * dragFactor + 40 * t * t;
      g.globalAlpha = 1 - t;
      g.fillStyle = PARTICLE_COLORS[this.pColor[i]];
      g.beginPath();
      g.arc(x, y, 3.5 * (1 - t * 0.5), 0, Math.PI * 2);
      g.fill();
    }
    g.globalAlpha = 1;
  }
}
